import { Container, Sprite } from 'pixi.js';
import { rnd } from './util';
import { SPRITES } from './sprites';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  r: number;
  color: number;
  fric: number;
  alive: boolean;
  view: Sprite;
}

const POOL_SIZE = 800;

export class ParticleSystem {
  private pool: Particle[] = [];
  private layer: Container;

  constructor(stage: Container) {
    this.layer = new Container();
    this.layer.blendMode = 'add';
    stage.addChild(this.layer);
  }

  private acquire(): Particle | null {
    for (const p of this.pool) {
      if (!p.alive) return p;
    }
    if (this.pool.length >= POOL_SIZE) return null;
    const view = new Sprite(SPRITES.particleGlow);
    view.anchor.set(0.5, 0.5);
    this.layer.addChild(view);
    const p: Particle = {
      x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 0,
      r: 1, color: 0xffffff, fric: 1, alive: false, view,
    };
    this.pool.push(p);
    return p;
  }

  burst(
    x: number, y: number, count: number,
    color: number, speed = 200, life = 0.5,
    angSpread = Math.PI * 2, angCenter = 0,
    rMin = 1, rMax = 3, fric = 0.92,
  ): void {
    for (let i = 0; i < count; i++) {
      const p = this.acquire();
      if (!p) return;
      const a = angCenter + (Math.random() - 0.5) * angSpread;
      const sp = rnd(speed * 0.5, speed);
      p.x = x; p.y = y;
      p.vx = Math.cos(a) * sp;
      p.vy = Math.sin(a) * sp;
      p.life = p.maxLife = rnd(life * 0.5, life);
      p.r = rnd(rMin, rMax);
      p.color = color;
      p.fric = fric;
      p.alive = true;
      // Sprite uses 64×64 baked gradient; scale to 2.6× radius for halo equivalence
      const s = (p.r * 2.6) / 32;
      p.view.scale.set(s, s);
      p.view.tint = color;
      p.view.visible = true;
    }
  }

  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.alive) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= Math.pow(p.fric, dt * 60);
      p.vy *= Math.pow(p.fric, dt * 60);
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        p.view.visible = false;
        continue;
      }
      p.view.visible = true;
      p.view.x = p.x;
      p.view.y = p.y;
      const t = p.life / p.maxLife;
      p.view.alpha = t * t;
    }
  }
}
